<script lang="ts" module>
  export interface Chip {
    id: string;
    label: string;
    /** Highlights the chip: a filter is applied, or this period is selected. */
    active?: boolean;
    /** A ▾ after the label: the chip opens a picker sheet instead of toggling. */
    caret?: boolean;
    /** Accessible name when it differs from the label. */
    ariaLabel?: string;
    /** Omit for informational period chips (as in the desktop Focus header). */
    onSelect?: () => void;
  }
</script>

<script lang="ts">
  /**
   * ChipRow — a horizontally scrolling row of pill chips (plans/mobile-v2.md §3.3):
   * filter chips that open a sheet (Tasks) and period chips (Focus). The row scrolls
   * on its own and never widens the page. Each chip has a 44px tall hit area around
   * the 34px pill.
   */
  interface Props {
    chips: Chip[];
    /** Accessible name of the group. */
    label: string;
    /** Focus: keep today's informational chip visible as the period changes. */
    revealActive?: boolean;
  }

  let { chips, label, revealActive = false }: Props = $props();
  let row: HTMLDivElement;
  // eslint-disable-next-line momentium/no-unguarded-effect -- DOM side effect only: reveal the current period inside its own horizontal scroller
  $effect(() => {
    const activeId = chips.find((chip) => chip.active)?.id;
    if (!revealActive || !activeId || !row) return;
    const active = row.querySelector<HTMLElement>('.on');
    if (active)
      row.scrollTo({
        left:
          row.scrollLeft +
          active.getBoundingClientRect().left -
          row.getBoundingClientRect().left -
          12,
      });
  });
</script>

<div bind:this={row} class="chip-row" role="group" aria-label={label}>
  {#each chips as chip (chip.id)}
    {#if !chip.onSelect}
      <span class="chip" class:on={chip.active} aria-current={chip.active ? 'date' : undefined}>
        <span class="pill">{chip.label}</span>
      </span>
    {:else}
      <button
        type="button"
        class="chip"
        class:on={chip.active}
        aria-label={chip.ariaLabel}
        aria-haspopup={chip.caret ? 'dialog' : undefined}
        aria-pressed={chip.caret ? undefined : (chip.active ?? false)}
        onclick={chip.onSelect}
      >
        <span class="pill">
          {chip.label}
          {#if chip.caret}<span class="caret" aria-hidden="true">▾</span>{/if}
        </span>
      </button>
    {/if}
  {/each}
</div>

<style>
  .chip-row {
    display: flex;
    gap: 2px;
    padding: 0 12px;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }

  .chip-row::-webkit-scrollbar {
    display: none;
  }

  .chip {
    flex: none;
    display: flex;
    align-items: center;
    height: 44px;
    padding: 0 3px;
    background: none;
    border: 0;
    font: inherit;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  span.chip {
    cursor: default;
  }

  .pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 34px;
    padding: 0 12px;
    color: #374151;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 999px;
  }

  .chip.on .pill {
    color: #1d4ed8;
    font-weight: 600;
    background: #eff6ff;
    border-color: #93c5fd;
  }

  .chip:active .pill {
    background: #f3f4f6;
  }

  .chip:focus-visible .pill {
    outline: 2px solid #3b82f6;
    outline-offset: 1px;
  }

  .caret {
    color: #9ca3af;
    font-size: 11px;
  }

  .chip.on .caret {
    color: #60a5fa;
  }
</style>
